import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import EmptyState from "@/components/EmptyState";
import { Star, AlertTriangle, ThumbsUp, FileImage } from "lucide-react";
import { fmtRelative } from "@/lib/format";

export default function ProjectQualityTab({ projectId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/quality`);
      setData(data);
    } finally { setBusy(false); }
  };
  useEffect(() => { if (projectId) load(); }, [projectId]);

  if (busy && !data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!data || data.total_review_items === 0) {
    return (
      <EmptyState
        title="No review items yet"
        description="Upload designs / websites / videos under Review items and ask reviewers to rate quality."
        icon={ThumbsUp}
        testid="project-quality-empty"
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="project-quality-tab">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="project-quality-summary">
        <Stat label="Review items" value={data.total_review_items} />
        <Stat label="Total ratings" value={data.total_ratings} />
        <Stat label="Avg ★" value={data.avg_project_stars ?? "—"} highlight />
      </div>

      <section className="border border-border" data-testid="project-quality-items">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-border label-eyebrow text-[10px] bg-muted/40">
          <div className="col-span-6">Item</div>
          <div className="col-span-2 text-center">Ratings</div>
          <div className="col-span-2 text-center">Avg ★</div>
          <div className="col-span-2 text-right">Last rated</div>
        </div>
        {data.items.map((it) => (
          <div key={it.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border items-center text-sm" data-testid={`pq-row-${it.id}`}>
            <div className="col-span-6 min-w-0 inline-flex items-center gap-2">
              <FileImage className="w-3.5 h-3.5 text-foreground/70 shrink-0" />
              <Link to={`/r/${it.id}`} className="font-medium truncate hover:text-primary">{it.title}</Link>
            </div>
            <div className="col-span-2 text-center">{it.rating_count}</div>
            <div className="col-span-2 text-center inline-flex items-center justify-center gap-1">
              {it.avg_stars != null && it.rating_count > 0
                ? <><Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" /> <span className="font-semibold">{it.avg_stars}</span></>
                : <span className="text-foreground/60">—</span>}
              {it.avg_stars != null && it.avg_stars < 3 && it.rating_count > 0 && (
                <AlertTriangle className="w-3 h-3 text-destructive ml-1" title="Below 3★ average" />
              )}
            </div>
            <div className="col-span-2 text-right text-xs">{it.last_rated_at ? fmtRelative(it.last_rated_at) : "—"}</div>
          </div>
        ))}
      </section>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`border ${highlight ? "border-primary/40 bg-primary/5" : "border-border"} p-4`}>
      <div className="label-eyebrow text-[10px] mb-1">{label}</div>
      <div className="text-3xl font-bold" style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
    </div>
  );
}
